import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import swal from 'sweetalert2';
import { BiX, BiPencil, BiCheck } from 'react-icons/bi';
import Loading from '../common/Loading';

const Overlay = styled.div`
  position: fixed;
  top: 0; left: 0; width: 100%; height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalBox = styled.div`
  background: white;
  border-radius: 16px;
  width: 640px;
  max-height: 85vh;
  overflow-y: auto;
  padding: 32px;
  position: relative;
  animation: fadeIn 0.2s ease;
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const CloseBtn = styled.div`
  position: absolute;
  top: 20px; right: 20px;
  cursor: pointer;
  color: #6b7280;
  &:hover { color: #111827; }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const Title = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 0;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #374151;
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 2px solid #e5e7eb;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 10px 12px;
  align-items: center;
`;

const Label = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
`;

const Value = styled.div`
  font-size: 14px;
  color: #111827;
`;

const EditInput = styled.input`
  font-size: 14px;
  color: #111827;
  padding: 6px 10px;
  border: 1.5px solid #d2d2d7;
  border-radius: 8px;
  outline: none;
  width: 100%;
  font-family: inherit;
  transition: border-color 0.2s;
  &:focus { border-color: #007aff; }
`;

const LevelSelect = styled.select`
  font-size: 14px;
  padding: 6px 10px;
  border: 1.5px solid #d2d2d7;
  border-radius: 8px;
  outline: none;
  font-family: inherit;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.2s;
  &:focus { border-color: #007aff; }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  background: ${({ $color }) => $color === 'green' ? '#ecfdf5' : $color === 'amber' ? '#fef3c7' : '#f3f4f6'};
  color: ${({ $color }) => $color === 'green' ? '#065f46' : $color === 'amber' ? '#92400e' : '#374151'};
`;

const OrderTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  th, td {
    padding: 8px 10px;
    font-size: 13px;
    border-bottom: 1px solid #f3f4f6;
    text-align: left;
  }
  thead tr { background: #2d3748; }
  th { font-weight: 700; color: #fff; }
  td { color: #111827; }
`;

const Btn = styled.button`
  padding: 8px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${({ $variant }) =>
    $variant === 'primary' ? '#007aff' :
    $variant === 'danger' ? '#fef2f2' : '#f3f4f6'};
  color: ${({ $variant }) =>
    $variant === 'primary' ? '#fff' :
    $variant === 'danger' ? '#dc2626' : '#374151'};
  &:hover {
    background: ${({ $variant }) =>
      $variant === 'primary' ? '#0066d6' :
      $variant === 'danger' ? '#fee2e2' : '#e5e7eb'};
  }
`;

const formatJoinDate = (objectId) => {
  if (!objectId) return '-';
  const ts = parseInt(objectId.toString().substring(0, 8), 16) * 1000;
  const d = new Date(ts);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}/${m}/${dd}`;
};

const formatTime = (timeStr) => {
  if (!timeStr) return '-';
  const match = timeStr.match(/(\d{4})\.(\d{2})\.(\d{2})-?(\d{2}):(\d{2}):(\d{2})/);
  if (match) return `${match[1].slice(2)}/${match[2]}/${match[3]} ${match[4]}:${match[5]}:${match[6]}`;
  return timeStr;
};

const levelLabel = (level) => {
  const map = { vip: 'VIP', school: 'School', level1: 'Level 1', level2: 'Level 2', level3: 'Level 3', shoppingmall: 'Shopping Mall' };
  return map[level] || level || '-';
};

const CompanySelect = styled.select`
  font-size: 14px;
  padding: 6px 10px;
  border: 1.5px solid #d2d2d7;
  border-radius: 8px;
  outline: none;
  font-family: inherit;
  background: #fff;
  cursor: pointer;
  max-width: 280px;
  transition: border-color 0.2s;
  &:focus { border-color: #007aff; }
`;

const CompanyBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  background: #f0f4ff;
  color: #1e40af;
`;

const UnassignedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  background: #fef3c7;
  color: #92400e;
`;

const UserDetailModal = ({ userId, onClose, onRefresh }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [companies, setCompanies] = useState([]);
  const [assigningCompany, setAssigningCompany] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [memo, setMemo] = useState('');
  const [memoSaved, setMemoSaved] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [userRes, companyRes] = await Promise.all([
          axios.get(`/admin/users/detail/${userId}`),
          axios.get('/admin/company/list'),
        ]);
        setData(userRes.data);
        setCompanies(companyRes.data);
        setForm({
          company_name: userRes.data.user.company_name || '',
          manager_name: userRes.data.user.manager_name || '',
          manager_contact: userRes.data.user.manager_contact || '',
          user_email: userRes.data.user.user_email || '',
          level: userRes.data.user.level || '',
        });
        setSelectedCompanyId(userRes.data.user.company_id || '');
        setMemo(userRes.data.user.admin_memo || '');
        setMemoSaved(userRes.data.user.admin_memo || '');
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userId]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    try {
      await axios.patch(`/admin/users/edit/${userId}`, form);
      swal.fire({ text: '수정되었습니다.', icon: 'success', showConfirmButton: false, timer: 1000 });
      // 로컬 데이터 갱신
      setData((prev) => ({
        ...prev,
        user: { ...prev.user, ...form },
      }));
      setEditing(false);
    } catch {
      swal.fire({ text: '오류가 발생했습니다.', icon: 'error', showConfirmButton: false, timer: 1000 });
    }
  };

  const handleCancel = () => {
    if (data?.user) {
      setForm({
        company_name: data.user.company_name || '',
        manager_name: data.user.manager_name || '',
        manager_contact: data.user.manager_contact || '',
        user_email: data.user.user_email || '',
        level: data.user.level || '',
      });
    }
    setEditing(false);
  };

  const handleResetPassword = async () => {
    const result = await swal.fire({
      text: '비밀번호를 [000000]으로 리셋하시겠습니까?',
      icon: 'warning', iconColor: '#d33',
      showCancelButton: true, confirmButtonColor: '#d33', cancelButtonColor: '#a3a3a3',
      confirmButtonText: '리셋', cancelButtonText: '취소',
    });
    if (result.isConfirmed) {
      try {
        await axios.patch(`/admin/users/password/${userId}`);
        swal.fire({ text: '리셋 완료', icon: 'success', showConfirmButton: false, timer: 1000 });
      } catch {
        swal.fire({ text: '오류가 발생했습니다.', icon: 'error', showConfirmButton: false, timer: 1000 });
      }
    }
  };

  const handleDelete = async () => {
    const step1 = await swal.fire({
      text: '이 회원을 삭제하시겠습니까?',
      icon: 'warning', iconColor: '#dc2626',
      showCancelButton: true, confirmButtonColor: '#dc2626', cancelButtonColor: '#a3a3a3',
      confirmButtonText: '삭제', cancelButtonText: '취소',
    });
    if (!step1.isConfirmed) return;
    const step2 = await swal.fire({
      text: '정말 삭제하시겠습니까? 휴지통에서 복원할 수 있습니다.',
      icon: 'warning', iconColor: '#dc2626',
      showCancelButton: true, confirmButtonColor: '#dc2626', cancelButtonColor: '#a3a3a3',
      confirmButtonText: '삭제 확인', cancelButtonText: '취소',
    });
    if (!step2.isConfirmed) return;
    try {
      await axios.patch(`/admin/trash/delete/user/${userId}`);
      swal.fire({ text: '삭제되었습니다.', icon: 'success', showConfirmButton: false, timer: 1000 });
      onClose();
    } catch {
      swal.fire({ text: '오류가 발생했습니다.', icon: 'error', showConfirmButton: false, timer: 1000 });
    }
  };

  const user = data?.user;
  const recentOrders = data?.recentOrders || [];
  const totalOrders = data?.totalOrders || 0;

  return (
    <Overlay onClick={handleOverlayClick}>
      <ModalBox>
        <CloseBtn onClick={onClose}><BiX size={24} /></CloseBtn>

        {loading ? (
          <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loading />
          </div>
        ) : !user ? (
          <div style={{ textAlign: 'center', color: '#999', padding: 40 }}>회원 정보를 불러올 수 없습니다.</div>
        ) : (
          <>
            <TitleRow>
              <Title>{editing ? '회원 정보 수정' : (user.company_name || '-')}</Title>
              {!editing && (
                <Btn onClick={() => setEditing(true)}>
                  <BiPencil size={16} /> 수정
                </Btn>
              )}
            </TitleRow>

            <Section>
              <SectionTitle>회원 정보</SectionTitle>
              <InfoGrid>
                <Label>회사명</Label>
                {editing ? (
                  <EditInput name="company_name" value={form.company_name} onChange={handleChange} />
                ) : (
                  <Value>{user.company_name}</Value>
                )}

                <Label>이메일</Label>
                {editing ? (
                  <EditInput name="user_email" value={form.user_email} onChange={handleChange} />
                ) : (
                  <Value>{user.user_email}</Value>
                )}

                <Label>담당자</Label>
                {editing ? (
                  <EditInput name="manager_name" value={form.manager_name} onChange={handleChange} />
                ) : (
                  <Value>{user.manager_name}</Value>
                )}

                <Label>연락처</Label>
                {editing ? (
                  <EditInput name="manager_contact" value={form.manager_contact} onChange={handleChange} />
                ) : (
                  <Value>{user.manager_contact}</Value>
                )}

                <Label>등급</Label>
                {editing ? (
                  <LevelSelect name="level" value={form.level} onChange={handleChange}>
                    <option value="vip">VIP</option>
                    <option value="level1">Level 1</option>
                    <option value="level2">Level 2</option>
                    <option value="level3">Level 3</option>
                    <option value="school">School</option>
                    <option value="mall">Shopping Mall</option>
                  </LevelSelect>
                ) : (
                  <Value><Badge $color="green">{levelLabel(user.level)}</Badge></Value>
                )}

                <Label>승인 상태</Label>
                <Value>
                  <Badge $color={user.approval ? 'green' : 'amber'}>
                    {user.approval ? '승인' : '대기'}
                  </Badge>
                </Value>

                <Label>소속 회사</Label>
                <Value>
                  {assigningCompany ? (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <CompanySelect
                        value={selectedCompanyId}
                        onChange={(e) => setSelectedCompanyId(e.target.value)}
                      >
                        <option value="">미배정</option>
                        {companies.map((c) => (
                          <option key={c._id} value={c._id}>{c.name}</option>
                        ))}
                      </CompanySelect>
                      <Btn $variant="primary" style={{ padding: '4px 12px', fontSize: 12 }} onClick={async () => {
                        try {
                          await axios.patch('/admin/company/assign-user', {
                            userId: user._id,
                            companyId: selectedCompanyId || null,
                          });
                          swal.fire({ text: '회사가 배정되었습니다.', icon: 'success', showConfirmButton: false, timer: 1000 });
                          setData((prev) => ({ ...prev, user: { ...prev.user, company_id: selectedCompanyId || null } }));
                          setAssigningCompany(false);
                        } catch {
                          swal.fire({ text: '오류가 발생했습니다.', icon: 'error', showConfirmButton: false, timer: 1000 });
                        }
                      }}>
                        <BiCheck size={14} /> 확인
                      </Btn>
                      <Btn style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => {
                        setSelectedCompanyId(user.company_id || '');
                        setAssigningCompany(false);
                      }}>취소</Btn>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {user.company_id ? (
                        <CompanyBadge>
                          {companies.find((c) => c._id === user.company_id)?.name || '알 수 없음'}
                        </CompanyBadge>
                      ) : (
                        <UnassignedBadge>미배정</UnassignedBadge>
                      )}
                      <Btn style={{ padding: '3px 10px', fontSize: 12 }} onClick={() => setAssigningCompany(true)}>
                        {user.company_id ? '변경' : '배정'}
                      </Btn>
                    </div>
                  )}
                </Value>

                <Label>가입일</Label>
                <Value>{formatJoinDate(user._id)}</Value>

                <Label>총 주문</Label>
                <Value>{totalOrders}건</Value>
              </InfoGrid>

              {editing && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                  <Btn onClick={handleCancel}>취소</Btn>
                  <Btn $variant="primary" onClick={handleSave}>
                    <BiCheck size={18} /> 저장
                  </Btn>
                </div>
              )}
            </Section>

            <Section>
              <SectionTitle>최근 주문 (최근 5건)</SectionTitle>
              {recentOrders.length > 0 ? (
                <OrderTable>
                  <thead>
                    <tr>
                      <th>발주번호</th>
                      <th>발주일</th>
                      <th>상품</th>
                      <th style={{ textAlign: 'right' }}>금액</th>
                      <th>상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentOrders.map((order) => {
                      const product = order.items && order.items.length > 0
                        ? order.items[0].product_name + (order.items.length > 1 ? ` 외 ${order.items.length - 1}건` : '')
                        : '-';
                      const isShipped = order.postNum && order.postNum !== '발주 확인 중';
                      return (
                        <tr key={order._id}>
                          <td style={{ fontFamily: "'SF Mono', monospace", fontSize: 12 }}>{order.order_id}</td>
                          <td>{formatTime(order.time)}</td>
                          <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product}</td>
                          <td style={{ textAlign: 'right', fontFamily: "'SF Mono', monospace" }}>
                            {order.verified_price ? order.verified_price.toLocaleString('ko-KR') : '-'}
                          </td>
                          <td>
                            <Badge $color={isShipped ? 'green' : 'amber'}>
                              {isShipped ? '출고완료' : '확인중'}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </OrderTable>
              ) : (
                <div style={{ color: '#9ca3af', fontSize: 14, padding: '20px 0', textAlign: 'center' }}>
                  주문 내역이 없습니다.
                </div>
              )}
            </Section>

            <Section>
              <SectionTitle>관리자 메모</SectionTitle>
              <textarea
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder="이 회원에 대한 관리자 메모를 입력하세요..."
                style={{
                  width: '100%', minHeight: 80, padding: '10px 12px',
                  border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14,
                  fontFamily: 'inherit', outline: 'none', resize: 'vertical',
                  lineHeight: 1.6,
                }}
              />
              {memo !== memoSaved && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                  <Btn $variant="primary" style={{ padding: '5px 14px', fontSize: 13 }} onClick={async () => {
                    try {
                      await axios.patch(`/admin/users/edit/${userId}`, { admin_memo: memo });
                      setMemoSaved(memo);
                      swal.fire({ text: '메모가 저장되었습니다.', icon: 'success', showConfirmButton: false, timer: 1000 });
                    } catch {
                      swal.fire({ text: '오류가 발생했습니다.', icon: 'error', showConfirmButton: false, timer: 1000 });
                    }
                  }}>메모 저장</Btn>
                </div>
              )}
            </Section>

            {!editing && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Btn $variant="danger" onClick={handleDelete}>
                  회원 삭제
                </Btn>
                <Btn $variant="danger" onClick={handleResetPassword}>
                  비밀번호 리셋
                </Btn>
              </div>
            )}
          </>
        )}
      </ModalBox>
    </Overlay>
  );
};

export default UserDetailModal;
